import { Check } from '@mui/icons-material'
import { Box, Button, CircularProgress, Divider, List, ListItem, Typography } from '@mui/material'
import { darken, lighten, useTheme } from '@mui/material/styles'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import usePatchConsentApprove from '../../api/v1/accounts/usePatchConsentApprove'
import { ExpirationHeader, Header } from '../../components/Layout'
import { useAuth } from '../../providers/auth'
import { ConsentStatus } from '../../types/Account'
import { extractAccountIDFromJwt } from '../../utils/jwt/jwt'

const DirectNoticePage: React.FC = () => {
  const { t } = useTranslation('vpc', { keyPrefix: 'directNotice' })
  const { authState, refreshAuthState } = useAuth()
  const {
    data,
    loading,
    error,
    callFetch: submitConsent,
  } = usePatchConsentApprove(extractAccountIDFromJwt(authState.jwt), authState.jwtRaw)
  const theme = useTheme()

  useEffect(() => {
    if (data && data.consent.status === ConsentStatus.APPROVED) {
      refreshAuthState()
    }
  }, [data, refreshAuthState])

  const listItemCss = { display: 'list-item', marginLeft: 2 }

  return (
    <Box
      data-testid="direct-notice-page"
      sx={{
        marginX: { xs: 2, md: 8, lg: 16 },
        textAlign: 'left',
        minHeight: '100vh',
      }}
      role="main"
    >
      <Header subtitle="" title={t('title', { keyPrefix: 'childLandingPage' })} />
      <ExpirationHeader />
      <Box component="section" sx={{ marginTop: 4 }}>
        <Typography variant="subtitle2">{t('ChildName')}</Typography>
        <Typography>{authState.accountInfo?.givenName}</Typography>
      </Box>
      <Typography sx={{ marginY: 2 }}>
        {t('NoticePart1', { name: authState.accountInfo?.givenName })}
      </Typography>
      <Box
        sx={{
          marginY: 2,
          maxHeight: '40vh',
          overflowY: 'scroll',
          overflowX: 'hidden',
          scrollbarGutter: 'stable',
          '&::-webkit-scrollbar': {
            width: theme.spacing(1),
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: theme.spacing(0.5),
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}
        tabIndex={0}
      >
        <Typography>{t('NoticePart2')}</Typography>
        <List sx={{ listStyleType: 'disc' }}>
          <ListItem sx={listItemCss}>{t('list.Login')}</ListItem>
          <ListItem sx={listItemCss}>{t('list.Contact')}</ListItem>
          <ListItem sx={listItemCss}>{t('list.Profile')}</ListItem>
          <ListItem sx={listItemCss}>{t('list.Device')}</ListItem>
          <ListItem sx={listItemCss}>{t('list.Purchases')}</ListItem>
        </List>
        <Typography sx={{ marginTop: 2 }}>{t('NoticePart3')}</Typography>
        <Typography sx={{ marginTop: 2 }}>{t('NoticePart4')}</Typography>
        <Typography sx={{ marginTop: 2 }}>{t('NoticePart5')}</Typography>
        <Typography sx={{ marginTop: 2 }}>{t('NoticePart6')}</Typography>
        <Typography sx={{ marginTop: 2 }}>{t('NoticePart7')}</Typography>
      </Box>
      <Divider />
      {!loading && error != null && (
        <Box sx={{ textAlign: 'center', marginTop: 4 }}>
          <Typography color="error">
            {t('generateCodeButton.error', { keyPrefix: 'confirmEmail' })}
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex' }}>
        <Button
          sx={{
            marginY: 4,
            marginX: 'auto',
            backgroundColor: theme.palette.success.main,
            color: 'white',
            pt: 1,
            pb: 2,
            px: 4,
            minWidth: theme.spacing(40),
            [theme.breakpoints.down('sm')]: {
              minWidth: '100%',
            },
            height: 'unset',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            '&:focus, &:hover': {
              background: darken(theme.palette.success.main, 0.35),
              boxShadow: 5,
            },
            '& .MuiTouchRipple-child': {
              backgroundColor: lighten(theme.palette.success.main, 0.2),
            },
          }}
          variant="contained"
          data-testid="consent-agree-button"
          onClick={submitConsent}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress sx={{ color: 'white' }} />
          ) : (
            <>
              <Check sx={{ color: 'white', fontSize: '3rem' }} />
              <Typography variant="buttonLarge" sx={{ color: 'white', fontSize: '18px' }}>
                {t('agree')}
              </Typography>
            </>
          )}
        </Button>
      </Box>
    </Box>
  )
}

export default DirectNoticePage
